import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { RoutingService } from './routing.service';
import { MicrosoftAccount } from '../_model/microsoft_account';
import { UserData } from '../_model/user_data';
import * as constants from '../_model/string_literal';
import { UserService } from './user.service';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  userOID = '';
  userName = '';
  email = '';
  userId = '';
  isAdmin = false;
  isUser = false;
  isThirdParty = false;
  isGlobalAdmin = false;
  isYakuin = false; // Your newly added role state
  notRegistered = true;
  isCS = false;

  // 1. Add a loading state subject to broadcast when authentication finishes
  private authReadySubject = new BehaviorSubject<boolean>(false);
  public authReady$: Observable<boolean> = this.authReadySubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: RoutingService,
    private userService: UserService,
  ) { }

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

getUserData() {
    this.http.get<MicrosoftAccount>(constants.MICROSOFT_ACCOUNT_ENDPOINT)
      .subscribe({
        next: (data) => {
          this.userOID = data.id;
        },
        error: (err) => {
          console.error('Failed to fetch Microsoft Account OID:', err);
          this.authReadySubject.next(false); // Release any locked waiting states
        },
        complete: () => {
          // 1. Authenticate with your iAPI using the OID
          this.loginToiAPI().subscribe({
            next: (data) => { 
              localStorage.setItem('token', JSON.stringify({ 'token': data }));
              
              // 2. CRITICAL CHANGE: Trigger the user details profile fetch *here* 
              // inside next() right after writing the token to ensure sequential safety.
              this.fetchAndProcessUser();
            },
            error: (err) => { 
              console.error('Login to iAPI failed:', err);
              this.authReadySubject.next(false);
              this.router.routingDashboard(true); 
            }
          });
        }
      });
  }

  // Helper method to keep your nesting readable and maintainable
  private fetchAndProcessUser() {
    const userRequest$ = this.getLoginUserData();
    
    if (!userRequest$) {
      console.error('Token could not be read from localStorage immediately.');
      this.authReadySubject.next(false);
      return;
    }

    userRequest$.subscribe({
      next: (userData) => {
        this.userName = userData.name;
        this.email = userData.email;
        this.userId = userData.id;
        this.checkRole(userData);
        
        // 3. SUCCESS: Token exists and User profile roles match. Broadcast to the views!
        this.authReadySubject.next(true);
        
        this.router.routingDashboard(this.notRegistered);
      },
      error: (err) => {
        console.error('Failed to retrieve logged-in user profile details:', err);
        this.authReadySubject.next(false); // Fail gracefully so views stop loading spin
      }
    });
  }

  checkRole(data: UserData) {
    // Set your roles and update this.isYakuin here based on role_id
    switch(data.role_id) {
      case 3:
      case 5:
        this.isGlobalAdmin = true;
        this.notRegistered = false;
        break;
      case 6: // Assuming 6 is Yakuin role
        this.isYakuin = true;
        this.notRegistered = false;
        break;
      default:
        this.notRegistered = true;
    }
  }

  loginToiAPI() {
    return this.http.post<{oid: string}>(constants.IAPI_ENDPOINT + '/api/auth/login/oid', JSON.stringify({oid: this.userOID}), this.httpOptions);
  }

  getLoginUserData() {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    return this.http.get<UserData>(constants.IAPI_ENDPOINT + '/user/logged/user', {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${JSON.parse(token)["token"]}`
      })
    });
  }
}